import React, { useState } from 'react';
import { Modal } from '../../../Components/Modal';
import { Button } from '../../../Components/Button';
import { Input } from '../../../Components/Input';
import { Title, Text } from '../../../Components/Typography';
import { PrecioCopia } from '../../../hooks/usePreciosCopias';
import './EditPriceModal.scss';

interface EditPriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  precio: PrecioCopia | null;
  onSave: (id: number, newPrice: number) => Promise<void>;
  loading: boolean;
}

export const EditPriceModal: React.FC<EditPriceModalProps> = ({
  isOpen,
  onClose,
  precio,
  onSave,
  loading
}) => {
  const [newPrice, setNewPrice] = useState<string>('');
  const [error, setError] = useState<string>('');

  React.useEffect(() => {
    if (precio) {
      setNewPrice(precio.precio.toString());
      setError('');
    }
  }, [precio]);

  const handleSave = async () => {
    if (!precio) return;

    const priceNumber = parseFloat(newPrice);
    
    if (isNaN(priceNumber) || priceNumber <= 0) {
      setError('El precio debe ser un número positivo');
      return;
    }

    try {
      await onSave(precio.id, priceNumber);
      onClose();
    } catch (err) {
      setError('Error al guardar el precio');
    }
  };

  const handleClose = () => {
    setNewPrice('');
    setError('');
    onClose();
  };

  if (!precio) return null;

  return (
    <Modal
      title="Editar Precio"
      open={isOpen}
      onClose={handleClose}
    >
      <div className="edit-price-modal">
        <div className="price-info">
          <div className="info-row">
            <Text bold>Color:</Text>
            <Text>{precio.color_name}</Text>
          </div>
          <div className="info-row">
            <Text bold>Papel:</Text>
            <Text>{precio.papel_name} ({precio.papel_weight}g)</Text>
          </div>
          <div className="info-row">
            <Text bold>Tamaño:</Text>
            <Text>{precio.hoja_name} ({precio.hoja_width} × {precio.hoja_height} mm)</Text>
          </div>
          <div className="info-row">
            <Text bold>Faz:</Text>
            <Text>{precio.faz_name}</Text>
          </div>
          <div className="info-row current-price">
            <Text bold>Precio Actual:</Text>
            <Text className="current-price-value">{precio.precio.toLocaleString('es-AR')}</Text>
          </div>
        </div>

        <div className="price-input-section">
          <Input
            title="Nuevo Precio"
            placeholder="Ingrese el nuevo precio"
            type="number"
            value={newPrice}
            onChange={setNewPrice}
            error={error}
            min={1}
          />
          <Text className="input-help">
            Ingrese solo números (sin símbolos de moneda)
          </Text>
        </div>

        <div className="modal-actions">
          <Button 
            onClick={handleClose}
            outlined
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            disabled={loading || !newPrice.trim()}
            loading={loading}
          >
            Guardar Cambios
          </Button>
        </div>
      </div>
    </Modal>
  );
};
